// SPDX-License-Identifier: AGPL-3.0-or-later
const NF = new Intl.NumberFormat('en-AU');

export function num(n: number | null | undefined, dp = 0): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return dp > 0
    ? n.toLocaleString('en-AU', { minimumFractionDigits: dp, maximumFractionDigits: dp })
    : NF.format(Math.round(n));
}

export function rate(n: number | null | undefined, dp = 1): string {
  return n === null || n === undefined || !Number.isFinite(n) ? '—' : n.toLocaleString('en-AU', { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

export function pct(n: number | null | undefined, dp = 1): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(dp)}%`;
}

/** Signed percentage, for change columns. */
export function delta(n: number | null | undefined, dp = 0): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  const s = (n * 100).toFixed(dp);
  return `${n > 0 ? '+' : ''}${s}%`;
}

/** "×" ratio, e.g. 16.8×. */
export function times(n: number | null | undefined, dp = 1): string {
  return n === null || n === undefined || !Number.isFinite(n) ? '—' : `${n.toFixed(dp)}×`;
}

/** Compact people count — 4.4m, 340k, 1,200. */
export function people(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  const a = Math.abs(n);
  if (a >= 1e6) return `${(n / 1e6).toFixed(1)}m`;
  if (a >= 1e4) return `${(n / 1e3).toFixed(0)}k`;
  return NF.format(Math.round(n));
}

/** Escape text destined for innerHTML. */
export function esc(s: unknown): string {
  return String(s ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  );
}

/* ── shared colours: one hue per concept, everywhere it appears ── */
export const COL = {
  total: '#334066', // indigo — the whole population
  atsi: '#b56a34', // ochre — Aboriginal & Torres Strait Islander (red earth, respectful, distinct)
  nonIndig: '#8b98b2', // muted slate — non-Indigenous
  sentenced: '#4b5a7e',
  remand: '#c08a2e', // gold — unsentenced / on remand
  male: '#486a92',
  female: '#9a6a94',
  prior: '#6b7f5a',
} as const;

/* ── states ── */
export const STATE_ORDER = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
export const STATE_NAME: Record<string, string> = {
  NSW: 'New South Wales', VIC: 'Victoria', QLD: 'Queensland', SA: 'South Australia',
  WA: 'Western Australia', TAS: 'Tasmania', NT: 'Northern Territory', ACT: 'Australian Capital Territory', AUS: 'Australia',
};
export const STATE_COLOUR: Record<string, string> = {
  NSW: '#3d6fb4', VIC: '#5a4b9c', QLD: '#b5642f', SA: '#b03a68',
  WA: '#c79a33', TAS: '#2f8f6b', NT: '#c85a3a', ACT: '#4f8a9c',
};
export const stateColour = (s: string): string => STATE_COLOUR[s] ?? 'var(--text-tertiary)';

/* ── sequential ramps ── */
// Rate choropleth: pale → deep indigo (more imprisonment = deeper).
export const RATE_RAMP = ['#eef1f7', '#cdd6e8', '#9fb0d2', '#6a80b0', '#3a4f80'];
// Over-representation ratio: pale → deep plum.
export const RATIO_RAMP = ['#f3ecf4', '#dcc6e0', '#bd93c6', '#9463a6', '#6b3d80'];

/** Bucket a value against ratio-to-reference thresholds; returns a ramp colour. */
export function rampFor(value: number | null | undefined, thresholds: number[], ramp: string[]): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'var(--no-data)';
  let i = 0;
  while (i < thresholds.length && value >= thresholds[i]) i++;
  return ramp[i];
}
